import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { prepareGoogleAdsTagForAuth } from '@/analytics/google-ads-signup';
import { trackRetentionEvent } from '@/analytics/retention-events';
import { authClient } from '@/auth/client';
import { ensureAuthDeviceCookie } from '@/auth/client-auth-context';
import {
  getGoogleOneTapWorkflow,
  isGoogleOneTapPath,
} from '@/auth/google-one-tap-policy';
import { websiteConfig } from '@/config/website';
import { clientEnv } from '@/env/client';
import { getOAuthCallbackUrlWithAttribution } from '@/attribution/auth-callback';
import { getCanonicalPathname } from '@/lib/locale';

type GoogleIdentityApi = {
  accounts?: {
    id?: {
      cancel?: () => void;
    };
  };
};

type GoogleOneTapPromptNotification = {
  isDismissedMoment?: () => boolean;
  isSkippedMoment?: () => boolean;
  getDismissedReason?: () => string;
  getSkippedReason?: () => string;
};

function getPromptOutcome(notification?: GoogleOneTapPromptNotification) {
  if (notification?.isDismissedMoment?.()) {
    return {
      moment: 'dismissed',
      reason: notification.getDismissedReason?.() ?? 'unknown',
    };
  }
  if (notification?.isSkippedMoment?.()) {
    return {
      moment: 'skipped',
      reason: notification.getSkippedReason?.() ?? 'unknown',
    };
  }
  return { moment: 'unknown', reason: 'unavailable' };
}

export function GoogleOneTap() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: session, isPending } = authClient.useSession();
  const lastPromptRequestPathRef = useRef<string | null>(null);
  const userId = session?.user?.id;
  const enabled =
    websiteConfig.auth?.enableGoogleLogin &&
    Boolean(clientEnv.VITE_GOOGLE_CLIENT_ID) &&
    isGoogleOneTapPath(pathname);

  useEffect(() => {
    if (!enabled || isPending || userId) return;

    const eventParams = {
      loginMethod: 'google_one_tap',
      path: getCanonicalPathname(pathname),
      workflow: getGoogleOneTapWorkflow(pathname),
    };
    let credentialTracked = false;
    let failureTracked = false;
    const trackFailure = (failureStage: string) => {
      if (failureTracked) return;
      failureTracked = true;
      trackRetentionEvent('google_one_tap_failure', {
        ...eventParams,
        failureStage,
        isAuthenticated: false,
      });
    };

    ensureAuthDeviceCookie();
    prepareGoogleAdsTagForAuth();
    if (lastPromptRequestPathRef.current !== pathname) {
      lastPromptRequestPathRef.current = pathname;
      trackRetentionEvent('google_one_tap_prompt_request', {
        ...eventParams,
        isAuthenticated: false,
      });
    }

    void authClient
      .oneTap({
        onPromptNotification: (
          notification?: GoogleOneTapPromptNotification
        ) => {
          trackRetentionEvent('google_one_tap_prompt_outcome', {
            ...eventParams,
            ...getPromptOutcome(notification),
            isAuthenticated: false,
          });
        },
        fetchOptions: {
          onRequest: () => {
            if (credentialTracked) return;
            credentialTracked = true;
            trackRetentionEvent('google_one_tap_credential', {
              ...eventParams,
              isAuthenticated: false,
            });
          },
          onSuccess: () => {
            trackRetentionEvent('google_one_tap_success', {
              ...eventParams,
              isAuthenticated: true,
            });
            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.href =
              getOAuthCallbackUrlWithAttribution(currentPath);
          },
          onError: () => trackFailure('credential_exchange'),
        },
      })
      .catch(() => {
        trackFailure(credentialTracked ? 'credential_exchange' : 'prompt');
      });

    return () => {
      const google = (window as Window & { google?: GoogleIdentityApi }).google;
      google?.accounts?.id?.cancel?.();
    };
  }, [enabled, isPending, pathname, userId]);

  return null;
}
